import React from "react";
import Button from "@/components/Common/Buttons/Button";
import { formatDate, formatPlainTextToHtml } from "@/utils/generalUtilities";
import {
  useGetAllLegalDocumentsQuery,
  LegalDocumentType,
} from "@/store/legalDocumentsApiSlice";
import { LEGAL_DOCUMENT_CONFIG } from "../constants";
import { TEMP_LEGAL_CONTENT } from "@/utils/tempLegalContent";

interface LegalDocumentModalProps {
  documentType: LegalDocumentType;
  onCancel: () => void;
  cancelIcon: string;
}

const LegalDocumentModal: React.FC<LegalDocumentModalProps> = ({
  documentType,
  onCancel,
  cancelIcon,
}) => {
  // Use the RTK Query hook to get legal documents
  const {
    data: legalDocuments,
    isLoading: loading,
    error: queryError,
  } = useGetAllLegalDocumentsQuery();

  const legalTerms = legalDocuments ? legalDocuments[documentType] : undefined;

  // Use temporary content only for the content field
  const tempContent = TEMP_LEGAL_CONTENT[documentType];
  const formattedTempContent = tempContent
    ? formatPlainTextToHtml(tempContent)
    : "";

  const error = queryError
    ? "Failed to load legal documents"
    : !legalTerms && !loading
    ? "Document not found"
    : null;

  const config = LEGAL_DOCUMENT_CONFIG[documentType];

  return (
    <div className="relative">
      <div className="sticky top-0 flex justify-between py-[16px] px-[24px] border-b-[1px] border-[#E2E2E2] bg-white">
        <h3 className="text-[18px] font-semibold font-outfit">
          {config.title}
        </h3>
        <img
          src={cancelIcon}
          onClick={onCancel}
          className="cursor-pointer w-[18px] h-[18px]"
          alt="Close"
        />
      </div>

      <div className="bg-[#F9F9F9] p-[24px]">
        {loading ? (
          <div className="space-y-2">
            <div className="h-4 bg-gray-300 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-300 rounded animate-pulse w-5/6"></div>
            <div className="h-4 bg-gray-300 rounded animate-pulse w-4/5"></div>
            <div className="h-4 bg-gray-300 rounded animate-pulse w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded animate-pulse w-5/6"></div>
          </div>
        ) : error ? (
          <div className="p-4 bg-red-100 text-red-700 rounded">{error}</div>
        ) : (
          <>
            <div className="text-[16px] leading-[150%] text-[#848484]">
              <p>
                The Sootchy {legalTerms?.title} was last updated and published
                on {formatDate(legalTerms?.publishedAt || "")}.
              </p>
              <p>Read before continuing.</p>
            </div>

            <div
              style={{
                boxShadow:
                  "0px 4px 16px 0px rgba(0, 0, 0, 0.04), 0px 2px 3px 0px rgba(0, 0, 0, 0.05)",
              }}
              className="my-[16px] bg-white p-[16px] rounded-[8px]"
            >
              <div
                className="font-outfit whitespace-pre-wrap text-[16px] leading-[200%]"
                dangerouslySetInnerHTML={{
                  __html: formattedTempContent || legalTerms?.content || "",
                }}
              />
            </div>
          </>
        )}
      </div>

      <div className="border-t-[1px] border-[#E2E2E2] sticky bottom-0 px-[24px] pt-[8px] bg-white ">
        <Button
          size="small"
          ariaLabel="previous"
          variant="secondary"
          label="Close"
          iconVisibility="hide"
          handleClick={onCancel}
        />
      </div>
    </div>
  );
};

export default LegalDocumentModal;
